import React, { useState, useCallback } from 'react';
import type { PropsWithChildren } from 'react';
import TruncateMarkup from 'react-truncate-markup';

import { useTranslation } from 'next-i18next';

import RenderLineBreaks from 'src/components/ui/RenderLineBreaks';
import Linkify from 'src/components/ui/Linkify';

import stopPropagation from 'src/utils/stopPropagation';
import { colours } from 'src/constants/colours';
import { styledTag } from 'src/utils/styled';

const StyledReadMore = styledTag('span')`
    cursor: pointer;
    &:hover {
        color: ${colours.secondary.main};
        text-decoration: underline;
    }
`;

const StyledTruncateMarkup = styledTag('p')<{
    $isInChatPreviewCard?: boolean;
}>`
    font-size: 1rem;
    font-family: 'Lato', sans-serif;
    font-weight: 400;
    line-height: 1.5;
    ${({ $isInChatPreviewCard }) =>
        $isInChatPreviewCard &&
        `
    font-size: 0.5rem;
    margin-top: 0.5rem;
    `}
`;

interface Props {
    text: string | React.ReactNode | React.ReactNode[];
    enableLinkify?: boolean;
    lines?: number;
    readLessText?: string;
    readMoreText?: string;
    showReadLess?: boolean;
    renderLineBreaksWhenClosed?: boolean;
    isInChatPreviewCard?: boolean;
    dataTargetId?: string;
    readMoreAction?: (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void | Promise<boolean | void>;
}

const ExpandableText: React.FC<Props> = ({
    text,
    lines = 4,
    readLessText,
    enableLinkify = false,
    readMoreText,
    showReadLess = false,
    renderLineBreaksWhenClosed = false,
    isInChatPreviewCard,
    dataTargetId = 'expandable-text',
    readMoreAction,
}) => {
    const { t } = useTranslation(['common']);
    const [isExpand, setExpand] = useState(false);

    const toggleExpand = (ev: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
        stopPropagation(ev);
        ev.preventDefault();
        setExpand(!isExpand);
    };

    const ClosedWrapper = renderLineBreaksWhenClosed ? RenderLineBreaks : React.Fragment;
    const LinkifyWrapper: React.FC<PropsWithChildren> = useCallback(
        ({ children }) => (enableLinkify ? <Linkify content={children} /> : (children as React.ReactElement)),
        [enableLinkify],
    );

    return isExpand ? (
        <RenderLineBreaks>
            <LinkifyWrapper>{text}</LinkifyWrapper>
            {showReadLess && (
                <>
                    {!readLessText && '... '}
                    <StyledReadMore tabIndex={0} role="button" onClick={toggleExpand} data-target-id={dataTargetId}>
                        {readLessText ?? t('common:general.readLess')}
                    </StyledReadMore>
                </>
            )}
        </RenderLineBreaks>
    ) : (
        <ClosedWrapper>
            <LinkifyWrapper>
                <TruncateMarkup
                    lines={lines}
                    ellipsis={
                        <>
                            {!readMoreText && '... '}
                            <StyledReadMore tabIndex={0} role="button" onClick={readMoreAction ?? toggleExpand}>
                                {readMoreText ?? t('common:general.readMore')}
                            </StyledReadMore>
                        </>
                    }
                >
                    <StyledTruncateMarkup
                        data-target-id={dataTargetId}
                        $isInChatPreviewCard={isInChatPreviewCard as boolean}
                    >
                        {text}
                    </StyledTruncateMarkup>
                </TruncateMarkup>
            </LinkifyWrapper>
        </ClosedWrapper>
    );
};

export default ExpandableText;
